/**
 * One box of a hand-built ICS card whose content is not a single text field.
 *
 * `SaveableField` covers a box that is one input. The 208's known hazards are
 * three checkbox grids, two custom blanks and a free-text line, and the printed
 * form treats all of that as box 5, so it gets one Save and Cancel rather than
 * six. This holds the whole box's draft and hands it to the caller to render.
 *
 * The draft is held locally so a refetch cannot overwrite half-finished work:
 * server state is taken only while the box has no unsaved edits, the same rule
 * `SaveableField` follows.
 */

import type React from 'react';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  refocusEditScope,
  useEditScopeFocus,
} from 'components/IAP/hooks/useEditScopeFocus';

export type SaveableBoxProps<T> = {
  id: string;
  /** Names the Save and Cancel buttons for screen readers. */
  label: string;
  value: T;
  onSave: (draft: T) => void;
  disabled?: boolean;
  children: (draft: T, setDraft: (next: T) => void) => React.ReactNode;
};

const sameValue = <T,>(a: T, b: T) => JSON.stringify(a) === JSON.stringify(b);

export function SaveableBox<T>({
  id,
  label,
  value,
  onSave,
  disabled = false,
  children,
}: SaveableBoxProps<T>) {
  const [draft, setDraft] = useState<T>(value);
  // What the box was last known to hold. Kept in state rather than a ref so
  // committing re-renders and the pair can settle.
  const [baseline, setBaseline] = useState<T>(value);
  const { focused, focusProps } = useEditScopeFocus();

  useEffect(() => {
    setDraft((current) => (sameValue(current, baseline) ? value : current));
    setBaseline(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(value)]);

  const dirty = !sameValue(draft, baseline);

  const handleSave = () => {
    setBaseline(draft);
    onSave(draft);
    refocusEditScope(id);
  };

  const handleCancel = () => {
    setDraft(baseline);
    refocusEditScope(id);
  };

  return (
    <div
      className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3"
      data-section={id}
      {...focusProps}
    >
      {children(draft, setDraft)}
      {/* Dirty keeps the pair on screen after focus moves away, so an edit is
          never left with no visible way to commit or discard it. */}
      {focused || dirty ? (
        <div className="tw:flex tw:justify-end tw:gap-2" data-section-actions>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled || !dirty}
            aria-label={`Cancel ${label}`}
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={disabled || !dirty}
            aria-label={`Save ${label}`}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default SaveableBox;
